/**
 * SupersetCard — Two exercises paired together in alternating sets
 *
 * Layout: A → B → rest → A → B → rest ...
 * Rest between exercises: 30–45s. Rest after completing one round: normal.
 * Volume and fatigue accounting is unchanged — each exercise logs
 * independently through the same logSet() API.
 */

import { useState, useCallback } from 'react';
import type { GeneratedExercise, LoggedSet, RPE, SuggestedSet } from '@/types';
import type { LogSetParams } from '@/hooks/useWorkoutSession';
import RestTimer from './RestTimer';

interface SupersetCardProps {
  exA: GeneratedExercise;
  exB: GeneratedExercise;
  indexA: number;
  indexB: number;
  loggedA: LoggedSet[];
  loggedB: LoggedSet[];
  audioEnabled: boolean;
  onLogSet: (params: LogSetParams) => void;
  onUnpair: () => void;
}

function SupersetSetRow({
  label,
  color,
  setNumber,
  suggested,
  logged,
  onLog,
  onRestStart,
}: {
  label: string;
  color: string;
  setNumber: number;
  suggested: SuggestedSet;
  logged: LoggedSet | undefined;
  onLog: (p: { setNumber: number; weightLbs: number; reps: number; rpe: RPE }) => void;
  onRestStart: (s: number) => void;
}) {
  const [weight, setWeight] = useState(logged?.weightLbs ?? suggested.suggestedWeightLbs);
  const [reps, setReps]     = useState(logged?.reps ?? suggested.targetReps);
  const [rpe, setRpe]       = useState<RPE>(logged?.rpe ?? suggested.targetRPE);
  const isLogged = !!logged;

  const handleLog = () => {
    if (isLogged) return;
    onLog({ setNumber, weightLbs: weight, reps, rpe });
    onRestStart(30); // short inter-exercise rest in superset
  };

  return (
    <div className={`flex items-center gap-2 py-2 px-3 rounded-xl
                     ${isLogged ? 'bg-success/5' : 'bg-surface-200'}`}>
      {/* Exercise label tag */}
      <div className={`flex-shrink-0 w-6 h-6 rounded-lg flex items-center justify-center
                       text-xs font-bold text-white`}
           style={{ background: color }}>
        {label}
      </div>

      {/* Weight */}
      <input type="number" value={weight} inputMode="decimal"
        onChange={(e) => setWeight(Number(e.target.value))}
        disabled={isLogged}
        className="flex-1 bg-surface-300 rounded-lg text-center text-base
                   font-bold font-mono text-ink py-2 min-h-tap
                   focus:outline-none focus:ring-2 focus:ring-accent
                   disabled:opacity-60 disabled:bg-transparent" />

      <span className="text-ink-faint text-sm">×</span>

      {/* Reps */}
      <input type="number" value={reps} inputMode="numeric"
        onChange={(e) => setReps(Number(e.target.value))}
        disabled={isLogged}
        className="w-14 bg-surface-300 rounded-lg text-center text-base
                   font-bold font-mono text-ink py-2 min-h-tap
                   focus:outline-none focus:ring-2 focus:ring-accent
                   disabled:opacity-60 disabled:bg-transparent" />

      {/* RPE */}
      <select value={rpe} onChange={(e) => setRpe(Number(e.target.value) as RPE)}
        disabled={isLogged}
        className="w-12 bg-surface-300 rounded-lg text-center text-sm font-bold
                   text-ink py-2 min-h-tap focus:outline-none disabled:opacity-60
                   disabled:bg-transparent">
        {[6,7,8,9,10].map((r) => <option key={r} value={r}>{r}</option>)}
      </select>

      {/* Log button */}
      <button
        disabled={isLogged}
        onClick={handleLog}
        className={`w-10 h-10 rounded-xl flex items-center justify-center
                    transition-all active:scale-90
                    ${isLogged ? 'bg-success/20 text-success' : 'bg-accent text-white'}`}>
        {isLogged ? '✓' : '→'}
      </button>
    </div>
  );
}

export default function SupersetCard({
  exA, exB,
  loggedA, loggedB,
  audioEnabled,
  onLogSet, onUnpair,
}: SupersetCardProps) {
  const [showTimer, setShowTimer] = useState(false);
  const [timerDuration, setTimerDuration] = useState(30);

  const totalSets = Math.max(exA.workingSets.length, exB.workingSets.length);
  const loggedCount = loggedA.filter((s) => !s.isWarmup).length
                    + loggedB.filter((s) => !s.isWarmup).length;

  const loggedAByNum = new Map(loggedA.map((s) => [s.setNumber, s]));
  const loggedBByNum = new Map(loggedB.map((s) => [s.setNumber, s]));

  const handleLogA = useCallback((p: { setNumber: number; weightLbs: number; reps: number; rpe: RPE }) => {
    onLogSet({ ...p, exerciseId: exA.exercise.id, isWarmup: false });
  }, [exA.exercise.id, onLogSet]);

  const handleLogB = useCallback((p: { setNumber: number; weightLbs: number; reps: number; rpe: RPE }) => {
    onLogSet({ ...p, exerciseId: exB.exercise.id, isWarmup: false });
  }, [exB.exercise.id, onLogSet]);

  const handleRestStart = useCallback((s: number) => {
    setTimerDuration(s);
    setShowTimer(true);
  }, []);

  const progressPct = totalSets > 0
    ? Math.min(100, (loggedCount / (totalSets * 2)) * 100)
    : 0;

  return (
    <>
      <div className="card space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="badge bg-accent/20 text-accent text-xs">Superset</div>
            <span className="text-xs text-ink-muted">{totalSets} rounds</span>
          </div>
          <button
            className="text-xs text-ink-faint hover:text-ink-muted active:scale-95
                       transition-all px-2 py-1 rounded-lg bg-surface-300"
            onClick={onUnpair}
          >
            Unpair
          </button>
        </div>

        {/* Exercise names */}
        <div className="flex gap-2">
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <div className="w-5 h-5 rounded flex-shrink-0 bg-accent flex items-center
                            justify-center text-xs font-bold text-white">A</div>
            <p className="text-sm font-semibold text-ink truncate">{exA.exercise.name}</p>
          </div>
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <div className="w-5 h-5 rounded flex-shrink-0 bg-success flex items-center
                            justify-center text-xs font-bold text-white">B</div>
            <p className="text-sm font-semibold text-ink truncate">{exB.exercise.name}</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-surface-400 rounded-full overflow-hidden">
          <div className="h-full bg-accent rounded-full transition-all duration-500"
               style={{ width: `${progressPct}%` }} />
        </div>

        {/* Interleaved set rows */}
        <div className="space-y-1.5">
          {Array.from({ length: totalSets }, (_, i) => (
            <div key={i} className="space-y-1">
              <p className="text-xs text-ink-faint px-1">Round {i + 1}</p>
              {exA.workingSets[i] && (
                <SupersetSetRow
                  label="A" color="#7c3aed"
                  setNumber={i + 1}
                  suggested={exA.workingSets[i]}
                  logged={loggedAByNum.get(i + 1)}
                  onLog={handleLogA}
                  onRestStart={handleRestStart}
                />
              )}
              {exB.workingSets[i] && (
                <SupersetSetRow
                  label="B" color="#22c55e"
                  setNumber={i + 1}
                  suggested={exB.workingSets[i]}
                  logged={loggedBByNum.get(i + 1)}
                  onLog={handleLogB}
                  onRestStart={(s) => handleRestStart(i < totalSets - 1 ? s : exA.exercise.restSeconds)}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {showTimer && (
        <RestTimer
          durationSeconds={timerDuration}
          audioEnabled={audioEnabled}
          onComplete={() => setShowTimer(false)}
          onSkip={() => setShowTimer(false)}
        />
      )}
    </>
  );
}
